/**
 * Centrifugo channel names.
 *
 * Built and parsed here rather than interpolated at call sites. These strings decide
 * who receives what: `session:{id}` reaches every participant, while
 * `session:{id}:host` carries live tallies and the unmoderated Q&A queue. A typo that
 * publishes host data to the participant channel is a silent data leak, not a crash,
 * so the strings exist in exactly one place with tests around them.
 *
 * Callers take named fields rather than positional ids — `personal(a, b)` with the
 * arguments swapped is a whole bug class that an object parameter removes.
 */

export const channels = {
  /** Everyone in the session: slide changes, round open/close, revealed results. */
  session: (sessionId: string) => `session:${sessionId}`,

  /** Presenter and moderators only: live tallies, the Q&A queue before moderation. */
  host: (sessionId: string) => `session:${sessionId}:host`,

  /** One participant: their own score and rank, never anybody else's. */
  personal: ({ sessionId, participantId }: { sessionId: string; participantId: string }) =>
    `session:${sessionId}:p:${participantId}`,
} as const;

export type ChannelKind = "session" | "host" | "personal";

export type ParsedChannel =
  | { kind: "session"; sessionId: string }
  | { kind: "host"; sessionId: string }
  | { kind: "personal"; sessionId: string; participantId: string };

/**
 * Parse a channel name back into its parts, for the Centrifugo subscribe proxy —
 * which has to decide whether THIS participant may join the channel they asked for.
 * Returns null for anything unrecognised; an unparseable channel is never authorised.
 */
export function parseChannel(channel: string): ParsedChannel | null {
  const parts = channel.split(":");

  if (parts[0] !== "session" || !parts[1]) return null;
  const sessionId = parts[1];

  if (parts.length === 2) return { kind: "session", sessionId };
  if (parts.length === 3 && parts[2] === "host") return { kind: "host", sessionId };
  if (parts.length === 4 && parts[2] === "p" && parts[3]) {
    return { kind: "personal", sessionId, participantId: parts[3] };
  }
  return null;
}
